// Dok 15 – Skydds- och ordningsregler
// Generisk regelbank som alltid är utgångspunkten. Reglerna kan därefter göras
// projektspecifika med AI om projektet har en beskrivning.

export interface SafetyRuleDef {
  key: string;
  text: string;
  legalReference?: string;
  /** Regler som normalt alltid gäller är förvalda */
  defaultIncluded?: boolean;
}

export interface SafetyRuleCategoryDef {
  category: string;
  rules: SafetyRuleDef[];
}

export const SAFETY_RULES_TEMPLATE: SafetyRuleCategoryDef[] = [
  {
    category: "Allmänna ordningsregler",
    rules: [
      { key: "allm-1", text: "Arbetstider på arbetsplatsen är vardagar 07:00–16:00. Avvikande arbetstid ska godkännas av Bas-U i förväg.", defaultIncluded: true },
      { key: "allm-2", text: "Alla håller ordning på sin arbetsplats och städar efter sig löpande. Transportvägar och utrymningsvägar hålls fria.", legalReference: "AFS 2023:3", defaultIncluded: true },
      { key: "allm-3", text: "Rökning är endast tillåten på anvisad plats.", defaultIncluded: true },
      { key: "allm-4", text: "Nolltolerans mot alkohol och droger gäller. Slumpvisa kontroller kan förekomma.", defaultIncluded: true },
      { key: "allm-5", text: "Fotografering och publicering av bilder från arbetsplatsen kräver godkännande av byggherren.", defaultIncluded: false },
      { key: "allm-6", text: "Instruktioner från Bas-U och skyddsombud ska följas av alla på arbetsplatsen.", legalReference: "AFS 2023:3", defaultIncluded: true },
    ],
  },
  {
    category: "Behörighet och introduktion",
    rules: [
      { key: "beh-1", text: "Giltigt ID06 ska bäras synligt av alla som vistas på arbetsplatsen.", defaultIncluded: true },
      { key: "beh-2", text: "Alla ska genomgå arbetsmiljöintroduktion innan arbete påbörjas.", legalReference: "AFS 2023:1", defaultIncluded: true },
      { key: "beh-3", text: "Besökare anmäler sig hos platsledningen och vistas på arbetsplatsen endast i sällskap av behörig person.", defaultIncluded: true },
      { key: "beh-4", text: "Personalliggare ska fyllas i vid in- och utpassering.", defaultIncluded: true },
      { key: "beh-5", text: "Underentreprenörer ansvarar för att egen personal fått introduktion och känner till dessa regler.", defaultIncluded: true },
    ],
  },
  {
    category: "Personlig skyddsutrustning",
    rules: [
      { key: "psu-1", text: "Skyddshjälm, skyddsskor med spiktrampskydd och varselkläder klass 2 ska bäras inom hela arbetsområdet.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "psu-2", text: "Ögonskydd används vid kapning, slipning, borrning och arbete med kemiska produkter.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "psu-3", text: "Hörselskydd används vid bullrande arbete och i skyltade bullerzoner.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "psu-4", text: "Andningsskydd med rätt filter används vid damm-, rök- och kemikalieexponering.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "psu-5", text: "Fallskyddssele används där kollektivt fallskydd saknas och ska vara förankrad i godkänd förankringspunkt.", legalReference: "AFS 2023:3", defaultIncluded: true },
      { key: "psu-6", text: "Skadad skyddsutrustning får inte användas utan ska bytas omgående.", defaultIncluded: true },
    ],
  },
  {
    category: "Arbete på höjd, ställningar och lyft",
    rules: [
      { key: "hojd-1", text: "Vid fallrisk över 2 meter ska kollektivt fallskydd finnas. Räcken får inte demonteras utan Bas-U:s godkännande.", legalReference: "AFS 2023:3", defaultIncluded: true },
      { key: "hojd-2", text: "Ställningar får endast byggas, ändras och rivas av utbildad personal. Grön ställningsskylt ska finnas före användning.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "hojd-3", text: "Stege används endast för kortvarigt arbete och som tillträdesled, aldrig som arbetsplattform.", defaultIncluded: true },
      { key: "hojd-4", text: "Öppningar och håltagningar ska täckas och märkas eller förses med räcke direkt.", defaultIncluded: true },
      { key: "hojd-5", text: "Lyftanordningar och lyftredskap ska vara besiktigade och märkta. Vistelse under hängande last är förbjuden.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "hojd-6", text: "Signalman/lyftledare ska utses vid lyft med skymd sikt.", defaultIncluded: true },
    ],
  },
  {
    category: "El, heta arbeten och brandskydd",
    rules: [
      { key: "el-1", text: "Endast behörig elpersonal får utföra elarbeten. Provisorisk el ska vara utförd fackmässigt och jordfelsbrytare användas.", legalReference: "ELSÄK-FS 2022:1", defaultIncluded: true },
      { key: "el-2", text: "Arbete nära spänningsförande delar kräver arbetsbevis och spänningslöst tillstånd av eldriftansvarig.", legalReference: "ELSÄK-FS 2022:1", defaultIncluded: true },
      { key: "el-3", text: "Heta arbeten kräver giltigt certifikat, skriftligt tillstånd, brandvakt och efterbevakning i minst 60 minuter.", defaultIncluded: true },
      { key: "el-4", text: "Brandsläckare ska finnas lättåtkomlig vid arbetsplatsen och kontrolleras regelbundet.", defaultIncluded: true },
      { key: "el-5", text: "Brännbart material ska förvaras avskilt och mängden på arbetsplatsen hållas nere.", defaultIncluded: true },
    ],
  },
  {
    category: "Kemiska produkter, damm och buller",
    rules: [
      { key: "kem-1", text: "Kemiska produkter ska vara godkända och förtecknade. Säkerhetsdatablad ska finnas tillgängliga på arbetsplatsen.", legalReference: "AFS 2023:10", defaultIncluded: true },
      { key: "kem-2", text: "Kvartsdammande arbete kräver riskbedömning, punktutsug eller vattenbegjutning samt andningsskydd.", legalReference: "AFS 2023:10", defaultIncluded: true },
      { key: "kem-3", text: "Torrsopning är förbjuden – använd dammsugare med H-klassat filter.", defaultIncluded: true },
      { key: "kem-4", text: "Asbest- och PCB-misstänkt material får inte bearbetas. Arbetet stoppas och Bas-U kontaktas omedelbart.", legalReference: "AFS 2023:10", defaultIncluded: true },
      { key: "kem-5", text: "Bullrande arbeten ska planeras och aviseras så att exponering för andra minimeras.", legalReference: "AFS 2023:11", defaultIncluded: true },
    ],
  },
  {
    category: "Maskiner, fordon och transportvägar",
    rules: [
      { key: "mask-1", text: "Maskiner och fordon får endast framföras av behörig och utbildad förare med giltigt körtillstånd.", legalReference: "AFS 2023:11", defaultIncluded: true },
      { key: "mask-2", text: "Högsta hastighet inom arbetsområdet är 10 km/h.", defaultIncluded: true },
      { key: "mask-3", text: "Backning ska undvikas – använd backvarnare, kamera eller vakt.", defaultIncluded: true },
      { key: "mask-4", text: "Skyddsanordningar på maskiner får inte kopplas ur. Daglig tillsyn ska utföras före användning.", defaultIncluded: true },
      { key: "mask-5", text: "Gående och fordonstrafik ska hållas åtskilda där det är möjligt.", defaultIncluded: true },
    ],
  },
  {
    category: "Avspärrningar och tillträdesskydd",
    rules: [
      { key: "avsp-1", text: "Riskområden ska spärras av och skyltas. Avspärrningar får endast tas bort av den som satt upp dem.", defaultIncluded: true },
      { key: "avsp-2", text: "Arbetsområdet ska vara inhägnat och obehöriga hindras från tillträde.", legalReference: "AFS 2023:3", defaultIncluded: true },
      { key: "avsp-3", text: "Arbetsplatsen ska lämnas säkrad vid arbetsdagens slut – öppningar täckta, maskiner låsta och el frånslagen.", defaultIncluded: true },
      { key: "avsp-4", text: "Belysning ska vara tillräcklig i arbets-, transport- och utrymningsvägar.", defaultIncluded: true },
    ],
  },
  {
    category: "Tillbud, olycka och första hjälpen",
    rules: [
      { key: "till-1", text: "Alla olyckor, tillbud och riskobservationer rapporteras omgående till Bas-U.", legalReference: "AFS 2023:1", defaultIncluded: true },
      { key: "till-2", text: "Allvarlig olycka och allvarligt tillbud anmäls till Arbetsmiljöverket utan dröjsmål.", legalReference: "AFS 2023:1", defaultIncluded: true },
      { key: "till-3", text: "Första hjälpen-utrustning och hjärtstartare ska finnas och vara skyltad. Utbildad personal ska finnas på plats.", legalReference: "AFS 2023:2", defaultIncluded: true },
      { key: "till-4", text: "Utrymningsplan, återsamlingsplats och larmadress ska vara anslagna och kända av alla.", defaultIncluded: true },
      { key: "till-5", text: "Var och en har rätt och skyldighet att stoppa arbete som bedöms som farligt.", defaultIncluded: true },
    ],
  },
  {
    category: "Miljö, avfall och tredje man",
    rules: [
      { key: "milj-1", text: "Avfall källsorteras i anvisade kärl. Farligt avfall hanteras separat och dokumenteras.", defaultIncluded: true },
      { key: "milj-2", text: "Spill av olja eller kemikalier saneras omedelbart och rapporteras.", defaultIncluded: true },
      { key: "milj-3", text: "Tredje man ska skyddas från arbetets risker genom avskärmning, skyltning och säkra passager.", legalReference: "AFS 2023:3", defaultIncluded: true },
      { key: "milj-4", text: "Störande arbeten aviseras till närboende och verksamheter i god tid.", defaultIncluded: false },
      { key: "milj-5", text: "Tomgångskörning är inte tillåten längre än en minut.", defaultIncluded: false },
    ],
  },
];

export const SAFETY_RULE_STATUS_LABELS: Record<string, string> = {
  draft: "Utkast",
  active: "Gällande",
  archived: "Arkiverad",
};
